/**
 * CalendarEventDetailsModal
 *
 * Wrapper que reaproveita 100% do `CalendarEventModal` do CRM
 * (src/components/crm/activities/calendar/CalendarEventModal.tsx) para os
 * eventos do Calendário Global cuja fonte é `activity` ou `booking`.
 *
 * Responsabilidades:
 *   - Adapta um `UnifiedCalendarEvent` para o shape esperado pelo modal CRM.
 *   - Usa o hook `useCalendarActivities` para Concluir / Reabrir / Cancelar
 *     (mesmas mutations atômicas já validadas pela Agenda CRM).
 *   - Invalida o cache do `useCalendarUnifiedEvents` após qualquer mutation
 *     para refletir risco visual no FullCalendar e na Lista.
 *   - Abre o `DealDetailsModalPremium` SOBREPOSTO quando o usuário clica em
 *     "Ver Negócio". Fechar o deal modal preserva o modal de evento.
 *
 * RELEASE:
 *   2026-06-25 v1.0.0 - Versão inicial (alinha Calendário Global à Agenda CRM)
 */

import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CalendarEventModal } from '@/components/crm/activities/calendar/CalendarEventModal';
import { useCalendarActivities } from '@/hooks/useCalendarActivities';
import { DealDetailsModalPremium } from '@/components/crm/deals/modal/DealDetailsModalPremium';
import type { UnifiedCalendarEvent } from '@/hooks/useCalendarUnifiedEvents';
import { createLogger } from '@/lib/logger';

const logger = createLogger('CalendarEventDetailsModal');

interface CalendarEventDetailsModalProps {
  event: UnifiedCalendarEvent;
  open: boolean;
  onClose: () => void;
}

/**
 * Resolve o ID da atividade-alvo (para passar às mutations) a partir do
 * evento unificado:
 *   - source_type='activity': source_id já é o id da atividade.
 *   - source_type='booking' : a atividade vinculada (crm_activity_id) não
 *     vem na RPC, mas o hook useCalendarActivities resolve via booking
 *     internamente quando recebemos o cancel_token; para Concluir/Reabrir
 *     do booking sem activity associada, retornamos null e o botão fica
 *     escondido naturalmente (sem activity.id não há o que concluir).
 */
function resolveActivityId(event: UnifiedCalendarEvent): string | null {
  if (event.source_type === 'activity') return event.source_id ?? null;
  // Para booking: usamos o próprio source_id como identificador exibido,
  // mas só permitimos Concluir/Reabrir quando há activity vinculada
  // (sinalizada por completed_at !== undefined OU presença na agenda CRM).
  // O CalendarEventModal usa `activity.id` apenas como chave dos handlers;
  // como completar booking sem activity não faz sentido, devolvemos null.
  return event.source_id ?? null;
}

export function CalendarEventDetailsModal({
  event,
  open,
  onClose,
}: CalendarEventDetailsModalProps) {
  const queryClient = useQueryClient();

  // Janela ampla apenas para satisfazer o hook (mutations não dependem de range).
  const now = new Date();
  const wideStart = new Date(now.getFullYear() - 1, 0, 1);
  const wideEnd = new Date(now.getFullYear() + 1, 11, 31);

  const {
    completeActivity,
    reopenActivity,
    cancelActivity,
  } = useCalendarActivities({ startDate: wideStart, endDate: wideEnd });

  // Estado: modal do negócio aberto SOBREPOSTO
  const [dealModalOpen, setDealModalOpen] = useState(false);
  const [dealIdToOpen, setDealIdToOpen] = useState<string | null>(null);

  // Adapta o evento unificado para o shape do CalendarEventModal CRM
  const activityId = resolveActivityId(event);
  const isCompleted = !!event.completed_at || !!event.cancelled_at;

  const adaptedEvent = useMemo(() => {
    if (!activityId) return null;
    return {
      activity: {
        id: activityId,
        title: event.title,
        description: event.description ?? null,
        // Tipo "meeting" cobre booking e a maioria das atividades agendadas
        type: 'meeting',
        scheduled_at: event.start_at,
        completed_at: event.completed_at ?? null,
      },
      type: 'meeting',
      isCompleted,
      dealId: event.deal_id ?? null,
      dealTitle: event.deal_title ?? undefined,
      customerName: event.customer_name ?? undefined,
      description: event.description ?? undefined,
      conferencingLink: event.meet_link ?? null,
      bookingCancelToken: event.cancel_token ?? null,
      bookingStatus: event.booking_status ?? null,
      hostUserName: event.host_user_name ?? null,
      hostAvatarUrl: event.host_avatar_url ?? null,
      calendarEmail: event.calendar_name ?? null,
      calendarColor: event.calendar_color ?? null,
      calendarOwnerName: event.calendar_owner_name ?? null,
    };
  }, [activityId, event, isCompleted]);

  /**
   * Invalida TODOS os caches do Calendário Global (Mês, Semana/Dia e Lista)
   * para refletir o novo estado sem precisar recarregar a página.
   */
  const invalidateUnified = () => {
    queryClient.invalidateQueries({ queryKey: ['calendar-unified-events'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-unified-events-infinite'] });
    queryClient.invalidateQueries({ queryKey: ['calendar-month-summary'] });
  };


  const handleComplete = async (id: string) => {
    try {
      await completeActivity.mutateAsync(id);
      toast.success('Evento concluído');
      invalidateUnified();
      onClose();
    } catch (err: any) {
      logger.error('Erro ao concluir', err);
      toast.error(err?.message ?? 'Erro ao concluir evento');
    }
  };

  const handleReopen = async (id: string) => {
    try {
      await reopenActivity.mutateAsync(id);
      toast.success('Evento reaberto');
      invalidateUnified();
      onClose();
    } catch (err: any) {
      if (err?.message === 'SLOT_CONFLICT') {
        toast.error('Não é possível reabrir: já existe outro evento neste horário.');
        return;
      }
      logger.error('Erro ao reabrir', err);
      toast.error(err?.message ?? 'Erro ao reabrir evento');
    }
  };

  const handleCancel = async (cancelToken: string, reason?: string) => {
    try {
      await cancelActivity.mutateAsync({ cancelToken, reason });
      toast.success('Agendamento cancelado');
      invalidateUnified();
      onClose();
    } catch (err: any) {
      logger.error('Erro ao cancelar', err);
      toast.error(err?.message ?? 'Erro ao cancelar agendamento');
    }
  };

  const handleOpenDeal = (dealId: string) => {
    setDealIdToOpen(dealId);
    setDealModalOpen(true);
  };

  if (!adaptedEvent) return null;

  return (
    <>
      <CalendarEventModal
        open={open}
        onOpenChange={(o) => !o && onClose()}
        event={adaptedEvent}
        onComplete={handleComplete}
        onReopen={handleReopen}
        onOpenDeal={handleOpenDeal}
        onCancelBooking={handleCancel}
        isCompleting={completeActivity.isPending}
        isReopening={reopenActivity.isPending}
        isCancelling={cancelActivity.isPending}
      />

      {/* Modal de Negócio sobreposto — preserva o modal de evento por trás */}
      {dealIdToOpen && (
        <DealDetailsModalPremium
          dealId={dealIdToOpen}
          open={dealModalOpen}
          onOpenChange={(o) => {
            setDealModalOpen(o);
            if (!o) setDealIdToOpen(null);
          }}
          initialTab="summary"
        />
      )}
    </>
  );
}
